import {
  CalendarMonth,
  DeleteOutlined as DeleteIcon,
  RestartAltOutlined as RestartAltIcon,
} from "@mui/icons-material";
import {
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from "@mui/material";
import { useCurrentUser } from "src/business-hooks/useCurrentUser";
import { LightBulbIcon } from "src/components/general/Icons/Icons";
import { useGlobalLocalContext } from "src/storage/localStorage";
import { HackerModeIcon } from "@ui-icons";
import {
  useTopbarDebugMenuAnchor,
  useTopbarDebugSectionTitleSx,
  useTopbarDebugUserSessionActions,
  useTopbarTenantAdminDebugActions,
} from "./TopbarDebugSettings.hooks";

interface TopbarDebugSettingsProps {
  isAutoTestsEnabled?: boolean;
}

const TopbarDebugSettings = ({ isAutoTestsEnabled }: TopbarDebugSettingsProps) => {
  const isDebugMode = useGlobalLocalContext((state) => state.isDebugMode);
  const loggedInUser = useCurrentUser();

  const { anchorEl, setAnchorEl, open, close } = useTopbarDebugMenuAnchor();
  const sectionTitleSx = useTopbarDebugSectionTitleSx();
  const { resetSessionAndStorage, resetUrlSearchAndHash, resetLocalPrefs } =
    useTopbarDebugUserSessionActions();
  const tenantAdmin = useTopbarTenantAdminDebugActions();

  return (
    <>
      {isDebugMode && (
        <Tooltip title="Debug Settings">
          <IconButton
            aria-label="Debug Settings"
            data-test-id="debug-settings-button"
            onClick={(event) => setAnchorEl(event.currentTarget)}
          >
            <HackerModeIcon />
          </IconButton>
        </Tooltip>
      )}
      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        open={open}
        onClose={close}
      >
        <Typography sx={sectionTitleSx}>User Settings</Typography>
        <Tooltip
          arrow
          title="Logs out, then wipes all local app state (global UI + saved feature prefs). Strongest reset."
          placement="left"
        >
          <MenuItem onClick={resetSessionAndStorage}>
            <ListItemIcon>
              <RestartAltIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Reset</ListItemText>
          </MenuItem>
        </Tooltip>
        <Tooltip
          arrow
          title="Forgets saved feature prefs in localStorage and strips those URL params so they won't restore later. Does not log out. Other URL params and hash stay."
          placement="left"
        >
          <MenuItem onClick={resetLocalPrefs} data-test-id="debug-reset-local-prefs">
            <ListItemIcon>
              <RestartAltIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Reset preferences</ListItemText>
          </MenuItem>
        </Tooltip>
        <Typography sx={sectionTitleSx}>Url state</Typography>
        <Tooltip
          arrow
          title="Strips all query params and the hash from this URL only. Does not log out or forget saved prefs — those can refill the URL on the next visit."
          placement="left"
        >
          <MenuItem onClick={resetUrlSearchAndHash}>
            <ListItemIcon>
              <RestartAltIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Reset</ListItemText>
          </MenuItem>
        </Tooltip>

        {loggedInUser?.is_tenant_admin && (
          <>
            <Divider />
            {isDebugMode && (
              <>
                <Typography sx={sectionTitleSx}>Admin</Typography>
                <MenuItem
                  onClick={() => {
                    void tenantAdmin.runInsights();
                  }}
                >
                  <ListItemIcon>
                    <LightBulbIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Run Insights (all)</ListItemText>
                </MenuItem>
                <MenuItem
                  disabled={!tenantAdmin.canScopeTenant}
                  onClick={() => {
                    void tenantAdmin.runInsightsForTenant();
                  }}
                >
                  <ListItemIcon>
                    <LightBulbIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Run Insights (this tenant)</ListItemText>
                </MenuItem>
                <MenuItem
                  disabled={!tenantAdmin.canScopeEnv}
                  onClick={() => {
                    void tenantAdmin.runInsightsForEnv();
                  }}
                >
                  <ListItemIcon>
                    <LightBulbIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Run Insights (this env)</ListItemText>
                </MenuItem>
                <MenuItem
                  disabled={!tenantAdmin.canScopeApp}
                  onClick={() => {
                    void tenantAdmin.runInsightsForApp();
                  }}
                >
                  <ListItemIcon>
                    <LightBulbIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Run Insights (this app)</ListItemText>
                </MenuItem>
                <MenuItem onClick={tenantAdmin.deleteEnv}>
                  <ListItemIcon>
                    <RestartAltIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Delete Env</ListItemText>
                </MenuItem>
                <MenuItem onClick={tenantAdmin.deleteAllAlerts}>
                  <ListItemIcon>
                    <RestartAltIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Delete Env Alerts</ListItemText>
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    void tenantAdmin.sendDailyIncidentsMail();
                  }}
                >
                  <ListItemIcon>
                    <CalendarMonth fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Send Daily Incidents Mail</ListItemText>
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    void tenantAdmin.sendMonthlySlaEmail();
                  }}
                >
                  <ListItemIcon>
                    <CalendarMonth fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Send monthly email</ListItemText>
                </MenuItem>
              </>
            )}
            {isDebugMode && (
              <>
                <Typography sx={sectionTitleSx}>Logics</Typography>
                <MenuItem onClick={tenantAdmin.deleteAllTests}>
                  <ListItemIcon>
                    <DeleteIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Delete All Tests</ListItemText>
                </MenuItem>
              </>
            )}
            <Typography sx={sectionTitleSx}>Auto Tests</Typography>
            {isDebugMode && (
              <>
                <MenuItem onClick={tenantAdmin.deleteEnvAutoTests}>
                  <ListItemIcon>
                    <DeleteIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Delete for Env</ListItemText>
                </MenuItem>
                <MenuItem
                  onClick={tenantAdmin.simulateAlerts}
                  disabled={!isAutoTestsEnabled}
                >
                  <ListItemIcon>
                    <RestartAltIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Simulate Env Alerts</ListItemText>
                </MenuItem>
              </>
            )}
          </>
        )}
      </Menu>
    </>
  );
};

export default TopbarDebugSettings;
